/**
 * Derives pie chart data from time-series chart data by summing dataset values
 * and grouping small values into an "Other" category.
 *
 * @param {Object} chartData - Chart.js format data with labels and datasets
 * @param {string[]} chartData.labels - Array of time-series labels
 * @param {Object[]} chartData.datasets - Array of dataset objects
 * @param {string} chartData.datasets[].label - Dataset label
 * @param {number[]} chartData.datasets[].data - Array of numeric values
 * @param {string[]} colors - Array of color codes to assign to datasets
 * @param {Object} [options] - Configuration options
 * @param {number} [options.threshold=0.01] - Minimum percentage (0-1) to avoid "Other" category
 * @param {number} [options.maxItems=5] - Maximum number of items before grouping into "Other"
 * @returns {Object[]} Array of pie data objects with label, value, percentage, and color
 *
 * @example
 * const pieData = derivePieData(
 *   { labels: ['Jan', 'Feb'], datasets: [{ label: 'A', data: [100, 200] }] },
 *   ['#0088FE'],
 *   { threshold: 0.05, maxItems: 5 }
 * )
 * // Returns: [{ label: 'A', value: 300, percentage: 100, color: '#0088FE' }]
 */
export function derivePieData (chartData, colors = [], options = {}) {
	// Apply defaults and validate
	let { threshold = 0.01, maxItems = 5 } = options

	// Validate maxItems
	if (maxItems < 1) {
		console.warn('derivePieData: maxItems must be at least 1, defaulting to 5')
		maxItems = 5
	}

	// Validate threshold
	if (threshold < 0 || threshold > 1) {
		console.warn('derivePieData: threshold must be between 0 and 1, defaulting to 0.01')
		threshold = 0.01
	}

	// Handle edge cases
	if (!chartData || !chartData.datasets || !Array.isArray(chartData.datasets)) {
		return []
	}

	if (chartData.datasets.length === 0) {
		return []
	}

	// Sum each dataset's values
	const datasetSums = chartData.datasets.map((dataset, index) => {
		const data = dataset.data || []
		const sum = data.reduce((acc, val) => {
			const numericValue = typeof val === 'number' && !isNaN(val) ? val : 0
			return acc + numericValue
		}, 0)

		return {
			label: dataset.label || `Dataset ${index + 1}`,
			value: sum,
			color: colors[index] || '#CCCCCC' // Default grey if no color provided
		}
	})

	// Calculate grand total
	const grandTotal = datasetSums.reduce((acc, item) => acc + item.value, 0)

	// Handle zero grand total
	if (grandTotal === 0) {
		return datasetSums.map(item => ({
			...item,
			percentage: 0
		}))
	}

	// Calculate percentages and sort by value descending
	const withPercentages = datasetSums
		.map(item => ({
			...item,
			percentage: (item.value / grandTotal) * 100
		}))
		.sort((a, b) => b.value - a.value)

	// Split into items to keep and items to group into "Other"
	const thresholdPercentage = threshold * 100
	const keepItems = []
	const otherItems = []

	withPercentages.forEach((item, index) => {
		// Keep items that are within maxItems AND above threshold
		// Items must satisfy BOTH conditions to be kept
		if (index < maxItems && item.percentage >= thresholdPercentage) {
			keepItems.push(item)
		} else {
			// Everything else goes to "Other"
			otherItems.push(item)
		}
	})

	// If we have items to group, create "Other" category
	if (otherItems.length > 0) {
		const otherValue = otherItems.reduce((acc, item) => acc + item.value, 0)
		const otherPercentage = (otherValue / grandTotal) * 100

		keepItems.push({
			label: 'Other',
			value: otherValue,
			percentage: otherPercentage,
			color: '#808080'
		})
	}

	// Handle edge case: all items went to "Other" (shouldn't happen with maxItems, but be safe)
	if (keepItems.length === 0 && otherItems.length > 0) {
		// Return the top item instead of having only "Other"
		const topItem = withPercentages[0]
		return [topItem]
	}

	return keepItems
}

/**
 * Converts derived pie data to Chart.js pie chart format.
 *
 * @param {Object[]} pieData - Array of pie data objects from derivePieData
 * @param {string} pieData[].label - Category label
 * @param {number} pieData[].value - Numeric value
 * @param {string} pieData[].color - Color code
 * @returns {Object} Chart.js compatible pie chart data object
 *
 * @example
 * const chartData = toPieChartFormat([
 *   { label: 'A', value: 300, percentage: 60, color: '#0088FE' },
 *   { label: 'B', value: 200, percentage: 40, color: '#00C49F' }
 * ])
 * // Returns: {
 * //   labels: ['A', 'B'],
 * //   datasets: [{
 * //     data: [300, 200],
 * //     backgroundColor: ['#0088FE', '#00C49F'],
 * //     borderColor: ['#0088FE', '#00C49F']
 * //   }]
 * // }
 */
export function toPieChartFormat (pieData) {
	// Handle edge cases
	if (!pieData || !Array.isArray(pieData) || pieData.length === 0) {
		return {
			labels: [],
			datasets: [{
				data: [],
				backgroundColor: [],
				borderColor: []
			}]
		}
	}

	// Extract labels, values, and colors
	const labels = pieData.map(item => item.label)
	const data = pieData.map(item => item.value)
	const backgroundColor = pieData.map(item => item.color)
	const borderColor = pieData.map(item => item.color)

	return {
		labels,
		datasets: [{
			data,
			backgroundColor,
			borderColor
		}]
	}
}
